import axios from "axios";
import { get, post } from "./base";

// AV-B3: Arps decline forecast for overlay on the Historical production curve.
// Contract (decline_analysis app mounted at /api/dca/):
//   GET /api/dca/arps/<target>/?by=well|asset[&phase=oil]
//   -> { b, Di, qi, EUR, unit, forecast: [{ date, rate }, ...] }
//
// Graceful degradation requirement: when the endpoint is not yet deployed
// (404 / network error / non-2xx), this MUST resolve to null WITHOUT throwing
// and WITHOUT triggering the global error Modal in ./base. We therefore call
// axios directly here instead of the shared `get` helper (which always pops a
// Modal.error on failure). The Authorization header is still injected by the
// axios request interceptor registered in ./base.
export function getArpsForecast(params, onSuccess, onError) {
  const { wellId, assetName, assetId, phase } = params || {};
  // Prefer a concrete well; fall back to asset. The path segment is the entity
  // id; the `by` query param tells BE how to interpret it (well|asset).
  let entity = null;
  let by = null;
  if (wellId != null) {
    entity = wellId;
    by = "well";
  } else if (assetId != null || assetName != null) {
    entity = assetId != null ? assetId : assetName;
    by = "asset";
  }
  if (entity == null) {
    onSuccess && onSuccess(null);
    return Promise.resolve(null);
  }

  const query = { by };
  if (phase) query.phase = phase;

  return axios
    .get(`/api/dca/arps/${encodeURIComponent(entity)}/`, { params: query })
    .then((res) => {
      onSuccess && onSuccess(res.data);
      return res.data;
    })
    .catch((error) => {
      // Endpoint not ready / failed: degrade silently (no Modal, no throw).
      onError && onError(error);
      onSuccess && onSuccess(null);
      return null;
    });
}

// DCA-C3: flow-regime diagnostics for the "diagnose-before-you-fit" view.
// Contract (decline_analysis app mounted at /api/dca/):
//   GET /api/dca/diagnostics/<target>/[?phase=oil][&by=asset]
//   -> {
//        loglog:    [{ t, q, deriv }, ...],   // t in yr, q rate, deriv = t*dq/dt (Bourdet)
//        rate_cum:  [{ np, q }, ...],          // cumulative vs rate
//        regimes:   [{ from, to, slope, flow_regime }, ...]  // identified segments
//      }
//
// Graceful degradation (same rationale as getArpsForecast): when the endpoint
// is not yet deployed (404 / network error / non-2xx), resolve to null WITHOUT
// throwing and WITHOUT triggering the global error Modal in ./base. We call
// axios directly; the Authorization header is still injected by the request
// interceptor registered in ./base.
export function getDcaDiagnostics(params, onSuccess, onError) {
  const { wellId, phase } = params || {};
  if (wellId == null) {
    onSuccess && onSuccess(null);
    return Promise.resolve(null);
  }

  const query = {};
  if (phase) query.phase = phase;

  return axios
    .get(`/api/dca/diagnostics/${encodeURIComponent(wellId)}/`, {
      params: query,
    })
    .then((res) => {
      onSuccess && onSuccess(res.data);
      return res.data;
    })
    .catch((error) => {
      // Endpoint not ready / failed: degrade silently (no Modal, no throw).
      onError && onError(error);
      onSuccess && onSuccess(null);
      return null;
    });
}

export function getRecommData(params, onSuccess, onError) {
  const data = {
    decline_phase: params.declinePhase,
    decline_model: params.declineModel,
    validation_months: params.validationMonths,
  };

  if (params.wellId) {
    data.uwi = params.wellId;
  }

  if (params.assetName) {
    data.asset_name = params.assetName;
  }
  if (params.startDate) {
    data.start_date = params.startDate;
  }

  post(
    "/api/dca/init/",
    data, null, {
    headers: {
      "Content-Type": "application/json",
    },
  },
    (res) => {
      onSuccess(res.data);
    },
    (error) => { console.error(error); onError(error) },
    false,
    true,
  );
}

export function getUserTuneData(params, onSuccess, onError) {
  const data = {
    decline_phase: params.declinePhase,
    decline_model: params.declineModel,
    start_date: params.startDate,
    validation_months: params.validationMonths,
    decline_rate: params.declineRate,
    ip_ratio: params.ipRatio,
    b: params.b,
  };

  // DCA-A3/A1 contract: terminal minimum decline (effective %/yr) for the
  // Modified-Hyperbolic switch. Sent only when the params panel supplied it;
  // BE (parallel DCA-A1) reads `d_min`, converts to nominal internally, and
  // ignores it gracefully until the field is wired server-side.
  if (params.dMin != null && params.dMin !== "") {
    data.d_min = params.dMin;
  }

  if (params.wellId) {
    data.uwi = params.wellId;
  }

  if (params.assetName) {
    data.asset_name = params.assetName;
  }

  post(
    "/api/dca/user_tune/",
    data, null, {
    headers: {
      "Content-Type": "application/json",
    },
  },
    (res) => {
      onSuccess(res.data);
    },
    (error) => { console.error(error); onError(error) }
  );
}

// DCA-A1 forecast response contract (consumed by pages/dca/DeclineHeroChart.js):
//   {
//     history, dca:{ predicted:[[date,val]…] },
//     switch: { switched:bool, date:"YYYY-MM-DD"|null, q:float|null },  // Dmin
//              // hyperbolic→exponential-tail switch; q in the forecast unit, date
//              // on the forecast calendar axis.
//     band:   { dates:["YYYY-MM"…], p90:[…], p50:[…], p10:[…], unit }   // monthly
//              // P10/P50/P90 rate band (p90 = conservative low), forecast-unit.
//   }
// Both keys are OPTIONAL — the chart degrades gracefully (shows a "pending
// backend field" hint) when a key is absent, and never re-scales the values.
export function getDcaForecast(params, onSuccess, onError) {
  const data = {
    decline_phase: params.declinePhase,
    decline_model: params.declineModel,
    start_date: params.startDate,
    forecast_months: params.forecastMonths,
    decline_rate: params.declineRate,
    ip_ratio: params.ipRatio,
    b: params.b,
  };

  // DCA-A3/A1: terminal minimum decline (effective %/yr) → Modified-Hyperbolic
  // exponential tail. Forwarded to the unified DCA engine; ignored gracefully
  // until BE wires `d_min` (parallel DCA-A1).
  if (params.dMin != null && params.dMin !== "") {
    data.d_min = params.dMin;
  }

  if (params.wellId) {
    data.uwi = params.wellId;
  }

  if (params.assetName) {
    data.asset_name = params.assetName;
  }

  post(
    "/api/dca/forecast/",
    data, null, {
    headers: {
      "Content-Type": "application/json",
    },
  },
    (res) => {
      onSuccess(res.data);
    },
    (error) => { console.error(error); onError(error) }
  );
}

export function getDcaManagementHistory(params, onSuccess, onError) {
  const data = {
    "decline_phase": params.declinePhase,
    "start_date": params.startDate,
    "validation_months": params.validationMonths,
    "mode": "sum",
    "params": params.params,
  };

  if (params.wellId) {
    data.uwi = params.wellId;
  } else {
    console.error("wellId is null!");
    onError("wellId is null!");
    return;
  }

  post(
    "/api/dca/ml_physics/validation/",
    data, null, {
    headers: {
      "Content-Type": "application/json",
    },
  },
    (res) => {
      onSuccess(res.data);
    },
    (error) => { console.error(error); onError(error) }
  );
}

export function getDcaManagementForecast(params, onSuccess, onError) {
  const data = {
    "decline_phase": params.declinePhase,
    "forecast_months": params.forecastMonths,
    "mode": "sum",
    "params": params.params,
    "params_values": params.paramsValues,
  };

  if (params.startDate) {
    data.start_date = params.startDate;
  }
  if (params.wellId) {
    data.uwi = params.wellId;
  } else {
    return;
  }

  post(
    "/api/dca/ml_physics/forecast/",
    data, null, {
    headers: {
      "Content-Type": "application/json",
    },
  },
    (res) => {
      onSuccess(res.data);
    },
    (error) => { console.error(error); onError(error) }
  );
}

export function dcaReportInit() {
  return get(
    `/api/report/dca/init`
  )
}

export function computeDCFS(data) {
  return post(
    `/api/report/dca/dcfs/`,
    data
  )
}

// WS6 RS-B: reserves report (PRMS/SEC) for the reporting page.
// Contract (RS-A1, POST /api/report/dca/reserves/):
//   in : { project_id, general:{ compliance_basis, effective_date, ... }, wells:[{ uwi }] }
//   out: { compliance_basis, effective_date, wells_reserves:[...], asset_summary, warnings:[...] }
// Uses the shared `post` (same silent-degrade / Modal behavior as computeDCFS):
// the returned promise resolves to the axios response (read `res.data`).
export function getReservesReport(data) {
  return post(
    `/api/report/dca/reserves/`,
    data
  )
}

// ---------------------------------------------------------------------------
// WS6 RS-D / RS-C2 — Reserves report version lifecycle + export + reconcile.
//
// These wrap the RS-D1 persistence endpoints and the RS-C2 export endpoint.
// Version CRUD / transitions use the shared `get`/`post` (Modal-on-error) since
// those are explicit user actions where a failure banner is appropriate.
//
// Export is special: it must (a) stream a file blob, (b) surface the ARIES
// "DRAFT" header without treating it as an error, and (c) turn PHDwin's HTTP
// 422 (pending real .phd samples) into a *friendly* structured result rather
// than the generic "Sending Data Error" Modal. We therefore call axios directly
// for export and normalize the outcome. The Authorization header is still
// injected by the request interceptor in ./base.
// ---------------------------------------------------------------------------

// POST /api/dca/reserves/versions/  → { ...version }
// snapshot = the already-computed reserves report response (res.data), optional.
export function saveReservesVersion(params) {
  const data = {
    project_id: params.projectId,
    effective_date: params.effectiveDate || undefined,
    compliance_basis: params.complianceBasis,
    reserves_snapshot: params.reservesSnapshot ?? undefined,
    evaluator_name: params.evaluatorName || undefined,
    evaluator_credentials: params.evaluatorCredentials || undefined,
    method_statement: params.methodStatement || undefined,
    as_of_note: params.asOfNote || undefined,
    change_note: params.changeNote || undefined,
  }
  return post(`/api/dca/reserves/versions/`, data)
}

// GET /api/dca/reserves/versions/?project_id=&effective_date=  → [ ...versions ]
export function listReservesVersions(params) {
  const query = {}
  if (params?.projectId != null) query.project_id = params.projectId
  if (params?.effectiveDate) query.effective_date = params.effectiveDate
  return get(`/api/dca/reserves/versions/`, query)
}

// GET /api/dca/reserves/versions/<id>/  → { ...version (with snapshot) }
export function getReservesVersion(versionId) {
  return get(`/api/dca/reserves/versions/${encodeURIComponent(versionId)}/`)
}

// POST /api/dca/reserves/versions/<id>/<action>/  (action: submit|approve|reject|lock)
export function transitionReservesVersion(versionId, action, note) {
  const allowed = ['submit', 'approve', 'reject', 'lock']
  if (!allowed.includes(action)) {
    return Promise.reject(new Error(`Unknown reserves version action: ${action}`))
  }
  const body = note ? { note } : {}
  return post(
    `/api/dca/reserves/versions/${encodeURIComponent(versionId)}/${action}/`,
    body
  )
}

// POST /api/dca/reserves/reconcile/  → { categories:{1P/2P/3P:{...}}, ... }
export function reconcileReserves(params) {
  const data = {
    project_id: params.projectId,
    prior_version_id: params.priorVersionId,
    current_version_id: params.currentVersionId,
  }
  return post(`/api/dca/reserves/reconcile/`, data)
}

// POST /api/report/dca/reserves/export?format=csv|xlsx|combocurve|aries|phdwin
//
// Resolves to a normalized object (never throws for the "expected" outcomes):
//   { ok: true,  blob, filename, draft: bool, format }             — file ready
//   { ok: false, pending: true, format, detail }                   — phdwin 422
//   { ok: false, error: true,   format, detail, status }           — real failure
//
// `draft` mirrors the ARIES DRAFT signal (response header X-Export-Draft or a
// filename/content-type hint). `data` is the reserves report payload (same shape
// posted to getReservesReport).
const EXPORT_EXT = {
  csv: 'zip', // csv export streams a zip bundle per the contract
  xlsx: 'xlsx',
  combocurve: 'zip',
  aries: 'zip',
  phdwin: 'zip',
}

function parseFilenameFromDisposition(disposition) {
  if (!disposition) return null
  // RFC 5987 (filename*=) first, then plain filename=
  const star = /filename\*=(?:UTF-8'')?["']?([^"';\n]+)/i.exec(disposition)
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1])
    } catch {
      return star[1]
    }
  }
  const plain = /filename=["']?([^"';\n]+)/i.exec(disposition)
  return plain && plain[1] ? plain[1] : null
}

// A 422 (or any error) arrives as a Blob because responseType=blob; read it back
// to JSON so we can show the friendly PHDwin message.
async function readBlobJson(blob) {
  if (!blob) return null
  try {
    const text = typeof blob.text === 'function' ? await blob.text() : null
    if (text == null) return null
    return JSON.parse(text)
  } catch {
    return null
  }
}

export function exportReservesReport(format, data) {
  const fmtKey = String(format || '').toLowerCase()
  return axios
    .post(`/api/report/dca/reserves/export`, data, {
      params: { format: fmtKey },
      responseType: 'blob',
    })
    .then((res) => {
      const headers = res.headers || {}
      // ARIES draft signal: dedicated header, or a generic "draft" marker in
      // content-type / content-disposition. Defensive across BE naming.
      const draftHeader =
        headers['x-export-draft'] ??
        headers['x-reserves-export-draft'] ??
        headers['x-draft']
      const disposition =
        headers['content-disposition'] || headers['Content-Disposition']
      const contentType = headers['content-type'] || ''
      const draft =
        String(draftHeader).toLowerCase() === 'true' ||
        draftHeader === '1' ||
        /draft/i.test(disposition || '') ||
        (fmtKey === 'aries' && /draft/i.test(contentType))
      const ext = EXPORT_EXT[fmtKey] || 'bin'
      const filename =
        parseFilenameFromDisposition(disposition) ||
        `reserves_export_${fmtKey}.${ext}`
      return { ok: true, blob: res.data, filename, draft: !!draft, format: fmtKey }
    })
    .catch(async (error) => {
      const status = error?.response?.status
      const body = await readBlobJson(error?.response?.data)
      // PHDwin: 422 = pending real .phd samples. Surface as a friendly pending
      // result rather than a hard error.
      if (status === 422) {
        return {
          ok: false,
          pending: true,
          format: fmtKey,
          status,
          detail:
            body?.detail ||
            body?.message ||
            body?.error ||
            'PHDwin export requires validated real .phd samples and is not yet available.',
          body,
        }
      }
      return {
        ok: false,
        error: true,
        format: fmtKey,
        status,
        detail:
          body?.detail ||
          body?.message ||
          body?.error ||
          error?.message ||
          'Export failed.',
        body,
      }
    })
}

export function exportDCAReportPDF(data) {
  return post(
    `/api/report/dca/gen`,
    data,
    {},
    {
      responseType: 'blob'
    }
  )
}

export function getDCAReportTemplates() {
  return get(
    '/api/report/dca/templates',
  )
}